// CardGridView.jsx
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Heading from "./Heading";
import KeyValue from "./KeyValue";
import { getIcon } from "./FuzzyIcon";
import { Card, Button } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";

/**
 * Modernized CardGridView
 * - Short fields: 3 per row
 * - Long fields: full width
 */

const styles = {
  container: { padding: 8 },
  card: {
    border: "none",
    borderRadius: 12,
    boxShadow: "0 4px 14px rgba(20,20,30,0.04)",
  },
  gridBase: {
    border: "none",
    padding: 10,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 120, // Slightly smaller min-width so 3 per row works reliably
  },
  gridFull: {
    border: "none",
    padding: 8,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
  },
  collapseBtn: {
    border: "none",
    boxShadow: "none",
    padding: "6px 10px",
  },
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return val;
};

const CardGridView = ({
  heading,
  title,
  fields = [],
  longFields = new Set(),
  info = {},
  foldable = false,
}) => {
  const [collapsed, setCollapsed] = useState(true);

  const longFieldSet = useMemo(() => {
    if (!longFields) return new Set();
    if (Array.isArray(longFields)) return new Set(longFields);
    if (longFields instanceof Set) return longFields;
    if (typeof longFields === "object") return new Set(Object.keys(longFields));
    return new Set();
  }, [longFields]);

  const prepared = useMemo(() => {
    const arr = (fields || []).map((raw) => {
      let field = raw;
      let label = raw;

      if (typeof raw === "string" && raw.includes("__")) {
        const [f, l] = raw.split("__");
        field = f;
        label = l;
      }

      const rawValue = Object.prototype.hasOwnProperty.call(info, field)
        ? info[field]
        : "";

      return {
        key: field,
        label,
        value: formatValue(rawValue),
        isLong: longFieldSet.has(field),
      };
    });

    const nonLong = arr.filter((it) => !it.isLong);
    const long = arr.filter((it) => it.isLong);
    return [...nonLong, ...long];
  }, [fields, info, longFieldSet]);

  const showItems = !foldable || (foldable && !collapsed);

  return (
    <>
      {title && (
        <div style={{ marginBottom: 8 }}>
          <Heading title={getIcon(title, true, { size: 28, title })} />
        </div>
      )}

      <div style={styles.container}>
        <Card
          style={styles.card}
          title={heading ? getIcon(heading, true, { size: 28, title: heading }) : null}
          extra={
            foldable ? (
              <Button
                onClick={() => setCollapsed((s) => !s)}
                aria-expanded={!collapsed}
                aria-controls="cardgrid-items"
                type="text"
                style={styles.collapseBtn}
              >
                {collapsed ? <DownOutlined /> : <UpOutlined />}
              </Button>
            ) : null
          }
        >
          <div id="cardgrid-items" role="region" aria-live="polite">
            {showItems && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {prepared.map((it) => {
                  const gridStyle = it.isLong ? styles.gridFull : styles.gridBase;

                  return (
                    <Card.Grid
                      key={`main-card-${it.key}`}
                      style={{
                        ...gridStyle,

                        // 🔥 EXACT FIX:
                        // - Short fields: 33.33% (3 per row)
                        // - Long fields: 100% (full width)
                        width: it.isLong ? "65%" : "32%",

                        borderRadius: 10,
                        background: "transparent",
                      }}
                    >
                      <KeyValue label={it.label} value={it.value} withIcon={true} />
                    </Card.Grid>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default CardGridView;
